import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from "react";

import type { APIBuildingImages, PrefillFormData } from "../model/types";
import type { SingleBuildingFormState } from "../model/types";

import { useGetBuildingImagesNoCache, useGetSubmittedBuildingInfoNoCache } from "../api/prefill";
import { convertPrefillImagesToFormData } from "../lib/prefillImagesToFormData";
import { convertPrefillFormDataToFormData } from "../lib/prefillToFormData";

export const usePrefillFormData = ({
  projectId,
  setFormData,
}: {
  projectId: string | undefined;
  setFormData: Dispatch<SetStateAction<SingleBuildingFormState>>;
}) => {
  const [pauseInteraction, setPauseInteractions] = useState(!!projectId);

  const buildingInfoRes = useGetSubmittedBuildingInfoNoCache(projectId, !!projectId);
  const submittedBuildingInfo = buildingInfoRes?.data as PrefillFormData | undefined;
  const formData = useMemo(() => convertPrefillFormDataToFormData(submittedBuildingInfo), [submittedBuildingInfo]);

  const imagesRes = useGetBuildingImagesNoCache(projectId);
  const submittedImages = imagesRes?.data as APIBuildingImages | undefined;
  const images = useMemo(() => convertPrefillImagesToFormData(submittedImages), [submittedImages]);

  useEffect(() => {
    if (formData) {
      setFormData((prev) => ({
        ...prev,
        ...formData,
        images: prev.images ?? [],
        prefilledOutline: formData.outline && formData.outline?.length > 0,
      }));
      setPauseInteractions(false);
    }
  }, [formData, setFormData, setPauseInteractions]);

  useEffect(() => {
    if (images)
      setFormData((prev) => ({
        ...prev,
        images,
      }));
  }, [images, setFormData]);

  return {
    isPrefillingFormData: buildingInfoRes.isLoading,
    pauseInteraction,
    setPauseInteractions,
  };
};
